import React, { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Input, Button, Card, Spin, message, Alert } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { GET_TASK_EXECUTION_BY_ID, GetTaskExecutionByIdResponse, TaskExecutionType } from './graphql/getTaskExecutionById';
import TaskDetailView from './components/TaskDetailView';
import MatrixHeatmap from './components/MatrixHeatmap';
import MatrixReorderProcess from './components/MatrixReorderProcess';
import QueryBlocksAnalysis from './components/QueryBlocksAnalysis';
import ExecutionTimeline from './components/ExecutionTimeline';

const PerformanceMonitor: React.FC = () => {
    const [taskId, setTaskId] = useState<string>('');
    const [taskData, setTaskData] = useState<TaskExecutionType | null>(null);
    const [parsedData, setParsedData] = useState<any>(null);

    const [getTaskExecution, { loading }] = useLazyQuery<GetTaskExecutionByIdResponse>(
        GET_TASK_EXECUTION_BY_ID,
        {
            fetchPolicy: 'network-only',
            onCompleted: (data) => {
                if (data?.getTaskExecutionById) {
                    setTaskData(data.getTaskExecutionById);
                    
                    // 解析message中的JSON数据
                    try {
                        if (data.getTaskExecutionById.message) {
                            const parsed = JSON.parse(data.getTaskExecutionById.message);
                            setParsedData(parsed);
                        }
                        message.success('任务记录加载成功');
                    } catch (e) {
                        message.error('数据解析失败');
                        console.error('JSON parse error:', e);
                    }
                } else {
                    message.error('未找到该任务记录');
                    setTaskData(null);
                    setParsedData(null);
                }
            },
            onError: (error) => {
                message.error(`查询失败: ${error.message}`);
                setTaskData(null);
                setParsedData(null);
            }
        }
    );

    const handleSearch = () => {
        const id = parseInt(taskId.trim(), 10);
        if (isNaN(id) || id <= 0) {
            message.warning('请输入有效的任务ID');
            return;
        }
        getTaskExecution({ variables: { id } });
    };

    const formatDateTime = (dateTime?: string) => {
        if (!dateTime) return '-';
        return dayjs(dateTime).format('YYYY-MM-DD HH:mm:ss');
    };

    return (
        <div className="p-6">
            <h1 className="text-2xl font-bold mb-6">OSRM距离矩阵性能检测</h1>

            <Card className="mb-6 w-1/4">
                <div className="flex gap-4 items-center">
                    <Input
                        placeholder="请输入任务ID"
                        value={taskId}
                        onChange={(e) => setTaskId(e.target.value)}
                        onPressEnter={handleSearch}
                        className="flex-1"
                        size="large"
                    />
                    <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        onClick={handleSearch}
                        loading={loading}
                        size="large"
                    >
                        查询
                    </Button>
                </div>
            </Card>

            {loading && (
                <div className="flex justify-center items-center py-20">
                    <Spin size="large" tip="加载中..." />
                </div>
            )}

            {taskData && parsedData && !loading && (
                <div className="space-y-6">
                    {/* 基础指标 */}
                    <TaskDetailView
                        taskId={taskData.id}
                        taskName={taskData.taskName}
                        startedAt={taskData.startedAt}
                        endedAt={taskData.endedAt}
                        durationSeconds={taskData.durationSeconds}
                        status={taskData.status}
                        data={parsedData}
                    />

                    {/* 缓存命中矩阵 */}
                    {parsedData.missing_pairs && (
                        <MatrixHeatmap
                            n={parsedData.n}
                            missingPairs={parsedData.missing_pairs}
                        />
                    )}

                    {/* 矩阵重排过程 (策略3专属) */}
                    {parsedData.strategy === 'matrix_reorder_partition' &&
                        parsedData.row_stats &&
                        parsedData.reorder_mapping &&
                        parsedData.missing_pairs && (
                            <MatrixReorderProcess
                                rowStats={parsedData.row_stats}
                                reorderMapping={parsedData.reorder_mapping}
                                partitionBoundary={parsedData.partition_boundary}
                                n={parsedData.n}
                                missingPairs={parsedData.missing_pairs}
                            />
                        )}

                    {/* 查询块分析 */}
                    {parsedData.partitions && parsedData.query_blocks && (
                        <QueryBlocksAnalysis
                            partitions={parsedData.partitions}
                            queryBlocks={parsedData.query_blocks}
                            totalMissing={parsedData.miss_count}
                            reorderMapping={parsedData.reorder_mapping}
                        />
                    )}

                    {/* 执行时间线 */}
                    {parsedData.timeline && (
                        <ExecutionTimeline
                            timeline={parsedData.timeline}
                            durationMs={parsedData.duration_ms}
                        />
                    )}

                    {/* Redis去重统计 */}

                    {/* 原始JSON数据 */}
                    <Card title="📋 原始数据 (JSON)">
                        <pre className="p-4 bg-gray-50 rounded overflow-auto max-h-96 text-xs">
                            {JSON.stringify(parsedData, null, 2)}
                        </pre>
                    </Card>

                    {/* 服务器信息 */}
                    <Card title="🖥️ 服务器信息" size="small">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-gray-600">服务器IP: </span>
                                <span className="font-mono">{taskData.serverIp || '-'}</span>
                            </div>
                            <div>
                                <span className="text-gray-600">主机名: </span>
                                <span className="font-mono">{taskData.hostname || '-'}</span>
                            </div>
                            <div>
                                <span className="text-gray-600">创建时间: </span>
                                <span>{formatDateTime(taskData.createdAt)}</span>
                            </div>
                            <div>
                                <span className="text-gray-600">更新时间: </span>
                                <span>{formatDateTime(taskData.updatedAt)}</span>
                            </div>
                        </div>
                    </Card>
                </div>
            )}

            {taskData && !parsedData && !loading && (
                <Alert
                    message="数据格式错误"
                    description="任务记录存在，但message字段数据格式不正确或为空"
                    type="warning"
                    showIcon
                />
            )}
        </div>
    );
};

export default PerformanceMonitor;
